import React, { useState, useEffect } from 'react';
import {
  Autocomplete,
  TextField,
  Box,
  Typography,
  CircularProgress,
} from '@mui/material';
import { COUNTRIES } from '@utils/countries';

interface Academy {
  id: string;
  academy_id_code?: string;
  name: string;
  country?: string;
  city?: string;
  address?: string;
}

interface AcademySearchProps {
  value: string | null;
  onChange: (academyId: string | null, academyName: string | null) => void;
  country?: string;
  city?: string;
  label?: string;
  required?: boolean;
}

const AcademySearch: React.FC<AcademySearchProps> = ({
  value,
  onChange,
  country,
  city,
  label = 'Academy',
  required = false,
}) => {
  const [academies, setAcademies] = useState<Academy[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchCountry, setSearchCountry] = useState<string | null>(country || null);
  const [searchCity, setSearchCity] = useState(city || '');
  const [selectedAcademy, setSelectedAcademy] = useState<Academy | null>(null);

  // Load academies based on search criteria
  useEffect(() => {
    const loadAcademies = async () => {
      try {
        setLoading(true);
        const params = new URLSearchParams({ status: 'active' });
        if (searchCountry) params.append('country', searchCountry);
        if (searchCity) params.append('city', searchCity);
        
        console.log('AcademySearch: Loading academies with params:', params.toString());
        const response = await fetch(`/api/academies/search?${params.toString()}`);
        
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        const data = await response.json();
        console.log('AcademySearch: Response:', data);
        
        if (data.success && data.data) {
          console.log('AcademySearch: Found', data.data.length, 'academies');
          setAcademies(data.data);
        } else {
          console.warn('AcademySearch: No academies found or API error:', data.message);
          setAcademies([]);
        }
      } catch (err: any) {
        console.error('Error searching academies:', err);
        console.error('Error details:', err.message);
        setAcademies([]);
      } finally {
        setLoading(false);
      }
    };

    // Always load academies (even with no filters, it will show all active academies)
    loadAcademies();
  }, [searchCountry, searchCity]);

  // Load selected academy if value is provided
  useEffect(() => {
    if (value && academies.length > 0) {
      const academy = academies.find(a => a.id === value);
      if (academy) {
        setSelectedAcademy(academy);
      }
    } else if (!value) {
      setSelectedAcademy(null);
    }
  }, [value, academies]);

  const handleAcademyChange = (newValue: Academy | null) => {
    setSelectedAcademy(newValue);
    onChange(newValue?.id || null, newValue?.name || null);
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
        <Autocomplete
          options={COUNTRIES}
          value={searchCountry}
          onChange={(_, newValue) => {
            setSearchCountry(newValue);
            setSelectedAcademy(null);
            onChange(null, null);
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Search by Country"
              placeholder="Select country"
              size="small"
              sx={{ flex: 1 }}
            />
          )}
        />
        <TextField
          label="Search by City"
          value={searchCity}
          onChange={(e) => {
            setSearchCity(e.target.value);
            setSelectedAcademy(null);
            onChange(null, null);
          }}
          placeholder="Enter city name"
          size="small"
          sx={{ flex: 1 }}
        />
      </Box>
      
      <Autocomplete
        options={academies}
        value={selectedAcademy}
        onChange={(_, newValue) => handleAcademyChange(newValue)}
        getOptionLabel={(option) => `${option.name}${option.city ? ` - ${option.city}, ${option.country}` : ''}`}
        isOptionEqualToValue={(option, value) => option.id === value?.id}
        loading={loading}
        renderInput={(params) => (
          <TextField
            {...params}
            label={label}
            required={required}
            placeholder={academies.length === 0 && !loading ? "No academies found. Try selecting a different country/city." : "Select an academy"}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {loading ? <CircularProgress color="inherit" size={20} /> : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
        renderOption={(props, option) => (
          <Box component="li" {...props} key={option.id}>
            <Box>
              <Typography variant="body1">{option.name}</Typography>
              <Typography variant="caption" color="text.secondary">
                {option.city && option.country ? `${option.city}, ${option.country}` : option.country || option.city || ''}
                {option.academy_id_code && ` • ID: ${option.academy_id_code}`}
              </Typography>
            </Box>
          </Box>
        )}
        disabled={false}
      />
    </Box>
  );
};

export default AcademySearch;

